/**
 * File: web/src/routes/Admin.tsx
 * =============================
 *
 * Admin route (approved email allowlist management)
 *
 * Purpose:
 * - Provide an admin-only UI for managing the set of approved uploader email addresses.
 * - Supports:
 *   - list allowlisted emails
 *   - add an email
 *   - remove an email
 *   - filter + sort results
 *
 * When used:
 * - Navigated to via /admin in the SPA.
 * - Requires:
 *   - user is authenticated (has token)
 *   - user is in Cognito group "Admins" (group claim in JWT)
 *
 * Data flow overview:
 * - UI calls API Gateway admin endpoints:
 *   - GET    /admin/approved-emails
 *   - POST   /admin/approved-emails
 *   - DELETE /admin/approved-emails/{email}
 * - Backend enforces auth via Cognito authorizer.
 * - Admin authorization is enforced by backend handler logic (and also hinted by client-side token check).
 *
 * Learner notes:
 * - Client-side isAdmin() is a *UX convenience* (hides admin UI for non-admins).
 *   The backend must still enforce admin privileges (never trust the client).
 * - Many older allowlist records may not have audit fields (addedAt/addedBy), so the UI treats them as optional.
 *
 * Gotchas:
 * - If token expires mid-session, api.ts will handle 401 by clearing token and redirecting to login.
 * - React hooks dependency arrays: this file intentionally triggers refresh only after token presence
 *   to avoid calling admin APIs before auth is ready.
 */

import { useEffect, useMemo, useState } from "react";
import type { AppConfig, ApprovedEmailItem } from "../lib/types";
import { getToken, isAdmin as isAdminToken, login } from "../lib/auth";
import {
  apiDelete,
  apiGet,
  apiPost,
  type ApprovedEmailsResponse,
} from "../lib/api";
import { useToast } from "../components/ToastProvider";

type SortDir = "asc" | "desc";
type SortKey = "email" | "addedAt" | "addedBy";

/**
 * Format epoch seconds into a readable local date/time.
 *
 * Why:
 * - Backend stores timestamps as epoch seconds (common cross-language format).
 * - UI uses Intl.DateTimeFormat so it respects the user’s locale.
 */
function fmtDate(epochSeconds?: number) {
  if (!epochSeconds) return "—";
  const d = new Date(epochSeconds * 1000);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function AdminRoute(props: { config: AppConfig }) {
  /**
   * Auth state:
   * - token determines whether we can call protected admin endpoints.
   * - isAdmin is derived from token claims (cognito:groups).
   *
   * Learner note:
   * - Even if isAdmin is true client-side, the backend must enforce admin checks too.
   * - Client-side checks are for UX; server-side checks are for security.
   */
  const token = getToken();
  const isAdmin = isAdminToken(token);

  // Toast notifications provide immediate feedback for actions (add/remove/fetch errors).
  const toast = useToast();

  /**
   * Server-provided data:
   * - items is the allowlist from the backend.
   * - msg provides a lightweight inline status ("Loading…", errors).
   */
  const [items, setItems] = useState<ApprovedEmailItem[]>([]);
  const [msg, setMsg] = useState("");

  // Controlled input for adding a new approved email.
  const [newEmail, setNewEmail] = useState("");

  // Search filter for the table.
  const [query, setQuery] = useState("");

  // Sorting controls for the table.
  const [sortKey, setSortKey] = useState<SortKey>("email");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  useEffect(() => {
    /**
     * Route guard + initial data load:
     *
     * - If token is missing, we start login immediately and stop.
     * - If token exists but user is not admin, we stop (no admin API calls).
     * - If admin, load the allowlist.
     *
     * Why login(config, '/admin'):
     * - Saves the post-login destination so after callback completes
     *   the app navigates back to /admin automatically.
     */
    if (!token) {
      login(props.config, "/admin");
      return;
    }

    // If signed in but not admin, show "Not authorized" notice; no API calls.
    if (!isAdmin) return;

    // Load allowlist data for admins.
    void refresh();

    /**
     * Note:
     * - Dependency array only includes [token] to avoid repeated refreshes.
     * - isAdmin depends on token; we short-circuit above.
     * - props.config is stable after runtime config loads.
     */
  }, [token]);

  /**
   * Refresh allowlist from backend.
   *
   * Data flow:
   * - GET /admin/approved-emails (requires admin)
   * - Response: { items: ApprovedEmailItem[] }
   */
  async function refresh() {
    try {
      setMsg("Loading…");
      const data = await apiGet<ApprovedEmailsResponse>(
        props.config,
        "/admin/approved-emails",
        token,
      );

      // Backends should return { items }, but we defensively default to [].
      setItems(data.items || []);
      setMsg("");
    } catch (e: any) {
      setMsg(e?.message || "Error");

      // Toast provides a stronger signal than inline status text alone.
      toast.push({
        kind: "error",
        title: "Failed to load list",
        message: e?.message || "Error",
      });
    }
  }

  /**
   * Add a new approved email.
   *
   * UX:
   * - Normalizes email (trim + lowercase) before sending.
   * - On success: clears input, shows toast, refreshes list.
   *
   * Learner note:
   * - Normalization is important because DynamoDB keys are case-sensitive.
   *   Keeping emails stored lower-case prevents duplicate logical identities.
   */
  async function addEmail() {
    const email = newEmail.trim().toLowerCase();
    if (!email) {
      setMsg("Enter an email address.");
      return;
    }

    try {
      setMsg("Adding…");
      await apiPost(props.config, "/admin/approved-emails", { email }, token);

      setNewEmail("");
      toast.push({
        kind: "success",
        title: "Approved email added",
        message: email,
      });

      // Refresh so UI shows the updated canonical list from the server.
      await refresh();
      setMsg("");
    } catch (e: any) {
      setMsg(e?.message || "Error");
      toast.push({
        kind: "error",
        title: "Add failed",
        message: e?.message || "Error",
      });
    }
  }

  /**
   * Remove an approved email.
   *
   * UX:
   * - confirm() prompt prevents accidental deletions.
   * - On success: toast + refresh list.
   *
   * Gotcha:
   * - encodeURIComponent(email) is essential because emails contain '@' and may contain '+'
   *   and other characters that must be URL-encoded to form a safe path segment.
   */
  async function removeEmail(email: string) {
    if (!confirm(`Remove ${email}?`)) return;

    try {
      setMsg("Removing…");
      await apiDelete(
        props.config,
        `/admin/approved-emails/${encodeURIComponent(email)}`,
        token,
      );

      toast.push({ kind: "success", title: "Removed", message: email });
      await refresh();
      setMsg("");
    } catch (e: any) {
      setMsg(e?.message || "Error");
      toast.push({
        kind: "error",
        title: "Remove failed",
        message: e?.message || "Error",
      });
    }
  }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  /**
   * Derived view: filter + sort.
   *
   * Why useMemo:
   * - Filtering/sorting can be O(n log n). For moderate lists it’s fine,
   *   but useMemo prevents recomputing unless inputs change.
   */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Filter by email or addedBy fields.
    const base = q
      ? items.filter(
          (it) =>
            it.email.toLowerCase().includes(q) ||
            String(it.addedBy || "")
              .toLowerCase()
              .includes(q),
        )
      : items;

    // Comparator based on selected sortKey.
    const cmp = (a: ApprovedEmailItem, b: ApprovedEmailItem) => {
      if (sortKey === "email")
        return (a.email || "").localeCompare(b.email || "");
      if (sortKey === "addedBy")
        return String(a.addedBy || "").localeCompare(String(b.addedBy || ""));
      return (a.addedAt || 0) - (b.addedAt || 0);
    };

    const sorted = base.slice().sort(cmp);
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [items, query, sortKey, sortDir]);

  return (
    <main id="main" className="container app-shell" role="main">
      <section className="card">
        <div className="card-header">
          <h1 className="card-title">Admin</h1>
          <p className="card-sub">
            Manage the list of approved uploader email addresses.
          </p>
        </div>
        <div className="card-body">
          {/* Auth gates:
              - no token: we started login() in useEffect; show "redirecting"
              - token but not admin: show "not authorized"
              - admin: show actual management UI
          */}
          {!token ? (
            <div className="notice">Redirecting to sign in…</div>
          ) : !isAdmin ? (
            <div className="notice">
              <strong>Not authorized.</strong> Your account is signed in but is
              not an admin.
            </div>
          ) : (
            <>
              {/* Controls row: refresh + sorting + filter */}
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div className="row">
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => void refresh()}
                  >
                    Refresh
                  </button>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() =>
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                    }
                  >
                    Sort: {sortDir === "asc" ? "Asc" : "Desc"}
                  </button>
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value as SortKey)}
                    style={{
                      borderRadius: 12,
                      padding: 12,
                      border: "1px solid var(--border)",
                    }}
                  >
                    <option value="email">Email</option>
                    <option value="addedAt">Added at</option>
                    <option value="addedBy">Added by</option>
                  </select>
                </div>
                <div style={{ minWidth: 260 }}>
                  <label
                    htmlFor="search"
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "var(--muted)",
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    Filter
                  </label>
                  <input
                    id="search"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search email or added-by…"
                    style={{
                      width: "100%",
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  />
                </div>
              </div>

              {/* Inline status message (loading/errors) */}
              {msg ? (
                <div className="notice" style={{ marginTop: 12 }}>
                  {msg}
                </div>
              ) : null}

              {/* Add email form */}
              <div
                className="row"
                style={{ marginTop: 12, alignItems: "flex-end" }}
              >
                <div style={{ minWidth: 280, flex: 1 }}>
                  <label
                    htmlFor="newEmail"
                    style={{
                      display: "block",
                      fontSize: 12,
                      color: "var(--muted)",
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    Email to approve
                  </label>
                  <input
                    id="newEmail"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="name@wsu.edu"
                    style={{
                      width: "100%",
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => void addEmail()}
                >
                  Add email
                </button>
              </div>

              {/* Results table */}
              <div style={{ marginTop: 16, overflowX: "auto" }}>
                {filtered.length === 0 ? (
                  <div className="notice">No results.</div>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="table" style={{ minWidth: 760 }}>
                      <thead>
                        <tr>
                          <th>
                            <button
                              type="button"
                              className="th-sort"
                              onClick={() => toggleSort("email")}
                              aria-sort={
                                sortKey === "email"
                                  ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                  : "none"
                              }
                            >
                              Email{sortIndicator("email")}
                            </button>
                          </th>

                          <th>
                            <button
                              type="button"
                              className="th-sort"
                              onClick={() => toggleSort("addedBy")}
                              aria-sort={
                                sortKey === "addedBy"
                                  ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                  : "none"
                              }
                            >
                              Added by{sortIndicator("addedBy")}
                            </button>
                          </th>

                          <th>
                            <button
                              type="button"
                              className="th-sort"
                              onClick={() => toggleSort("addedAt")}
                              aria-sort={
                                sortKey === "addedAt"
                                  ? sortDir === "asc"
                                    ? "ascending"
                                    : "descending"
                                  : "none"
                              }
                            >
                              Added at{sortIndicator("addedAt")}
                            </button>
                          </th>

                          <th style={{ width: 140, textAlign: "right" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((it) => (
                          <tr key={it.email}>
                            <td>{it.email}</td>
                            <td>{it.addedBy || "—"}</td>
                            <td>{fmtDate(it.addedAt)}</td>
                            <td style={{ width: 140, textAlign: "right" }}>
                              <button
                                className="btn btn-danger"
                                type="button"
                                onClick={() => void removeEmail(it.email)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="notice" style={{ marginTop: 12 }}>
                Approved emails added directly through the Cognito console may
                show “—” for Added by/at.
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
